import { Input, Label } from '@startupquickstart/frontend';
import { ImageUrlList } from './ImageUrlList';
import type { MinigameAuthoringPanelProps } from './types';

interface MemoryMatchConfig {
  cardImages?: string[];
  gridSize?: number;
  timeLimit?: number;
}

const DEFAULTS: Required<MemoryMatchConfig> = {
  cardImages: [],
  gridSize: 4,
  timeLimit: 60,
};

export function MemoryMatchPanel({
  value,
  onChange,
  disabled,
}: MinigameAuthoringPanelProps<MemoryMatchConfig>) {
  const v = { ...DEFAULTS, ...(value ?? {}) };
  const set = (patch: Partial<MemoryMatchConfig>) => onChange({ ...v, ...patch });

  // For an NxN grid we need N² / 2 unique images. Surface this so admins
  // know how many to upload.
  const cardsNeeded = Math.floor((v.gridSize * v.gridSize) / 2);

  return (
    <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
      <div className="space-y-1">
        <h4 className="text-sm font-semibold">Memory match</h4>
        <p className="text-xs text-muted-foreground">
          Card-flip grid. Find all pairs before the timer runs out.
        </p>
      </div>

      <ImageUrlList
        label="Card images"
        helpText={`Each image becomes a pair. A ${v.gridSize}×${v.gridSize} grid needs ${cardsNeeded} unique images.`}
        values={v.cardImages}
        onChange={(cardImages) => set({ cardImages })}
        disabled={disabled}
        min={cardsNeeded}
        max={50}
      />

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Grid size (NxN)</Label>
          <Input
            type="number"
            min={2}
            max={8}
            value={v.gridSize}
            disabled={disabled}
            onChange={(e) => set({ gridSize: Number(e.target.value) })}
          />
        </div>
        <div className="space-y-1">
          <Label>Time limit (sec, 0 = none)</Label>
          <Input
            type="number"
            min={0}
            max={3600}
            value={v.timeLimit}
            disabled={disabled}
            onChange={(e) => set({ timeLimit: Number(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
}
